import checkBranding from 'src/router/middleware/authentification/checkBranding.js'
import tracking from 'src/config/tracking/index.js'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'
import {
  createSduiRouter,
  setFirstTargetRouteAfterAppStart,
  setScreenReaderFocus
} from '@/router/routerUtils.js'

let isFirstRouting = true

// Create router instance, register all routes
const router = createSduiRouter()

router.beforeEach(async (to, from) => {
  const mwContext = {
    from,
    router,
    to,
    store: GlobalPrototypes.store
  }

  // Apply transition if route has a transition point
  const hasTransition = to.meta.transitionPoint !== undefined && from.meta.transitionPoint !== undefined
  const transitionName = hasTransition ? to.meta.transitionPoint < from.meta.transitionPoint ? 'slide-right' : 'slide-left' : ''
  GlobalPrototypes.store.commit('SET_TRANSITION_NAME', transitionName)

  // Global middleware enabling changing branding on public routes
  if (!from.name && to.meta.isPublic) {
    const result = checkBranding(mwContext)
    if (result) return result
  }

  // The first route navigation is triggered by registering the router in main.ts "app.use(router)".
  // During this first routing we check if the user can be fetched and authenticated which is only needed on non-public routes.
  // To prevent any flickering or showing error states, we abort navigation here and route to it again once it's done.
  if (isFirstRouting) {
    isFirstRouting = false

    // Initial target route will be executed again once the user handling is done.
    // Only defer private routes without hard redirects (window.location.href) — they need user loading before rendering.
    // Public routes (like /webuntis SSO callback) handle themselves immediately and must not be replayed after
    // user initialization.
    if (to.meta.isPublic && to.meta.isRouteHardRedirecting) return
    setFirstTargetRouteAfterAppStart(to)
    return
  }

  // Loop through middlewares defined in the route meta and execute them sequentially
  // If a middleware returns a value, it will be used as the next route to navigate to
  if (to.meta.middleware) {
    const middlewares = Array.isArray(to.meta.middleware)
      ? to.meta.middleware
      : [to.meta.middleware]

    for (let i = 0; i < middlewares.length; i++) {
      const result = await middlewares[i]({...mwContext})
      if (result !== undefined && result !== true) {
        return result
      }
    }
  }

  // Remove the dummy query parameter that was added to force routing to the same route
  if (to.query.firstValidRouting) return {...to, query: {...to.query, firstValidRouting: undefined}}
})

router.afterEach(async (to, from) => {
  tracking.handleRouteNavigation(to, from)

  await setScreenReaderFocus(to, from, GlobalPrototypes.store)
})

export default router
